import { Image as AntImage, Typography } from 'antd'
import styled, { css } from 'styled-components'

import { TimezonedDate, useTimezonedDates } from '../../context/timezoneContext'
import { getDocumentIcon } from '../attachments/attachmentHelpers'
import { getTicketStatus } from '../workers/mappers'

import { Button } from '@/components/shared-components/BUI/Button'
import { Flexbox } from '@/components/shared-components/Flexbox'
import PreventPageBreak, {
  HiddenInPrint,
} from '@/components/shared-components/PrintMedia'
import TicketCard from '@/components/shared-components/TicketCard'
import { useLocale } from '@/context/localeContext'

import { Ticket as TicketType } from 'types/api.graphql'

export type CardDisplay = {
  color: string
  textColor?: string
  stripe?: {
    position: 'top' | 'right'
    color: string
    textColor?: string
    text?: string
  }
}

export const ticketNameToKey = {
  'White Card': 'whiteCard',
  WWCC: 'wwcc',
}

export const getFallbackCardDisplay = (text: string): CardDisplay => ({
  color: '#282b3c',
  stripe: {
    position: 'right',
    color: 'transparent',
    textColor: '#ffffff',
    text,
  },
})

export const ticketCardDisplay: Record<string, Record<string, CardDisplay>> = {
  whiteCard: {
    default: {
      color: 'black',
      stripe: {
        position: 'right',
        color: '#FFBB34',
        textColor: 'white',
      },
    },
    VIC: {
      color: 'black',
      stripe: {
        position: 'right',
        color: '#FFBB34',
        textColor: 'white',
        text: 'Victoria',
      },
    },
    NSW: {
      color: '#4C3D80',
      stripe: {
        position: 'right',
        color: '#FEED36',
        textColor: '#4C3D80',
        text: 'New South Wales',
      },
    },
    ACT: {
      color: '#000000',
      stripe: {
        position: 'right',
        textColor: '#f7ed4b',
        color: '#017aba',
        text: 'ACT',
      },
    },
    QLD: {
      color: '#ffffff',
      textColor: '#000000',
      stripe: {
        position: 'right',
        color: '#a11238',
        textColor: '#ffffff',
        text: 'Queensland',
      },
    },
    SA: {
      color: '#ffffff',
      textColor: '#000000',
      stripe: {
        position: 'right',
        color: '#f33232',
        textColor: '#ffffff',
        text: 'South Australia',
      },
    },
    WA: {
      color: '#ffffff',
      textColor: '#000000',
      stripe: {
        position: 'right',
        color: '#f33232',
        textColor: '#ffffff',
        text: 'Western Australia',
      },
    },
    TAS: {
      color: '#ffffff',
      textColor: '#000000',
      stripe: {
        position: 'right',
        color: '#016d56',
        textColor: '#ffffff',
        text: 'Tasmania',
      },
    },
    NT: {
      color: '#ffffff',
      textColor: '#000000',
      stripe: {
        position: 'right',
        color: '#ec6c34',
        textColor: '#ffffff',
        text: 'Northern Territory',
      },
    },
  },
  wwcc: {
    default: {
      color: '#90C86E',
      stripe: {
        position: 'top',
        color: '#177CBC',
      },
    },
  },
}

const CARD_OFFSET = '6rem'

const TicketImage = styled(AntImage)`
  object-fit: contain;
`

const ImageCaption = styled.div`
  text-align: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
`

const SubItem = ({ children, title }) => (
  <div>
    <Typography.Text>{title}: </Typography.Text>
    <Typography.Text type="secondary">{children}</Typography.Text>
  </div>
)

type CardWrapperProps = {
  offset: string
  showBackground: boolean
}

const CardWrapper = styled(PreventPageBreak)<CardWrapperProps>`
  position: relative;
  display: flex;

  ${({ showBackground, offset }) =>
    showBackground &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: ${offset};
        bottom: 0;
        left: 0;
        right: 0;
        background-color: white;
        box-shadow:
          rgb(154 160 185 / 5%) 0px 5px 10px,
          rgb(166 173 201 / 20%) 0px 15px 40px;
        border: 1px solid #edf2f9;
        border-radius: 10px;
        z-index: -1;

        @media print {
          box-shadow: none;
        }
      }
    `}

  @media (max-width: 767px) {
    flex-direction: column;
  }
`

const CardContent = styled.div<{ offset: string }>`
  margin-top: ${({ offset }) => offset};
  padding-left: 2rem;
  flex: 1;

  @media (max-width: 767px) {
    margin-top: 0;
    padding-top: 2rem;
    padding-left: 0;
  }
`

const CardInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  > div {
    margin-right: 1.5rem;
  }
`

const DocumentButtonText = styled.div`
  font-size: 12px;
  text-align: center;
  color: rgba(0, 0, 0, 0.65);
`

const ImagesContainer = styled.div``
const Images = styled.div`
  display: flex;

  > div {
    margin-right: 1.5rem;
  }

  .ant-image {
    display: block;
  }
`

const ImageTitle = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.45);
  margin: 0.5rem 0 0;
`

const StyledTicketCard = styled(TicketCard)`
  flex: 1;
  align-self: flex-start;

  @media (max-width: 767px) {
    align-self: center;
  }
`

const TicketNumber = styled.span`
  text-transform: uppercase;
`

type Ticket = {
  id: TicketType['id']
  countryIssued?: TicketType['countryIssued']
  stateIssued?: TicketType['stateIssued']
  expiry?: TicketType['expiry']
  number?: TicketType['number']
  obtained?: TicketType['obtained']
  ticketTemplate: {
    name: TicketType['ticketTemplate']['name']
    shortName: TicketType['ticketTemplate']['shortName']
  }
  files?: {
    name: TicketType['files'][number]['name']
    file: {
      contentType: TicketType['files'][number]['file']['contentType']
      url?: TicketType['files'][number]['file']['url']
      name: TicketType['files'][number]['file']['name']
    }
  }[]
}

type Props = {
  ticket: Ticket
  showTicketInfo?: boolean
  className?: string
  ticketInfoOffset?: string
  ticketInfoBackground?: boolean
}

export const TicketCardRenderer: React.FC<Props> = ({
  ticket,
  showTicketInfo = true,
  className,
  ticketInfoOffset = CARD_OFFSET,
  ticketInfoBackground = true,
}) => {
  const { t } = useLocale()
  const { formatDateString } = useTimezonedDates()

  if (!ticket) return null

  const stateKey = ticket.stateIssued || 'default'

  const ticketData =
    ticketCardDisplay[ticketNameToKey[ticket.ticketTemplate.shortName]]

  const ticketStateData =
    ticketData?.[stateKey] ||
    (ticketData?.default
      ? {
          ...ticketData.default,
          stripe: {
            ...ticketData.default.stripe,
            text: ticket.stateIssued,
          },
        }
      : getFallbackCardDisplay(ticket.stateIssued))

  const statusInfo = { status: getTicketStatus(ticket.expiry) }

  const config = {
    ...ticketStateData,
    ...statusInfo,
  }

  return (
    <CardWrapper
      key={ticket.id}
      className={className}
      offset={ticketInfoOffset}
      showBackground={ticketInfoBackground}
    >
      <StyledTicketCard
        {...config}
        title={ticket.ticketTemplate.shortName}
        descriptor={ticket.ticketTemplate.name}
        number={ticket.number}
        expiry={
          ticket.expiry
            ? formatDateString({ date: ticket.expiry, includeTime: false })
            : undefined
        }
      />

      {showTicketInfo && (
        <CardContent offset={ticketInfoOffset}>
          <CardInfo>
            <SubItem title="Type">{ticket.ticketTemplate.shortName}</SubItem>
            {ticket.obtained && (
              <SubItem title="Date of Issue">
                <TimezonedDate date={ticket.obtained} includeTime={false} />
              </SubItem>
            )}
            {ticket.number && (
              <SubItem title={t('ticket:labels.ticketIdentifier')}>
                <TicketNumber>{ticket.number}</TicketNumber>
              </SubItem>
            )}
            {ticket.countryIssued && (
              <SubItem title="Country">{ticket.countryIssued}</SubItem>
            )}
            {ticket.stateIssued && (
              <SubItem title="State">{ticket.stateIssued}</SubItem>
            )}
          </CardInfo>

          <ImagesContainer>
            <Images>
              {ticket.files.map((file, i) => {
                const docTypes = [
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'pdf',
                  'doc',
                  'docx',
                ]

                const isDocument = docTypes.includes(file.file.contentType)

                return (
                  <Flexbox
                    key={i}
                    flexDirection="column"
                    justifyContent="flex-end"
                    gap={'10px'}
                  >
                    {!isDocument ? (
                      <>
                        <ImageTitle>
                          <HiddenInPrint>(click to expand)</HiddenInPrint>
                        </ImageTitle>
                        <TicketImage
                          height={100}
                          src={file.file.url}
                          alt={file.name}
                        />
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          window.open(
                            file.file.url,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }}
                      >
                        {getDocumentIcon(file.file.contentType)}
                        <DocumentButtonText>Open document</DocumentButtonText>
                      </Button>
                    )}
                    <ImageCaption>{file.file.name}</ImageCaption>
                  </Flexbox>
                )
              })}
            </Images>
          </ImagesContainer>
        </CardContent>
      )}
    </CardWrapper>
  )
}

export type CardTicket = Ticket
